/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2022 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */
/**
 * Footer styles
 *
 * @file Footerstyles.jsx
 * @author Poornima N
 * @since 0.0.1
 */
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import mixins from '../../assets/styles/mixins.scss';

const FOOTER_LINK_TYPOGRAPHY = {
  color: mixins.color_text_primary,
  fontSize: mixins.fontSize14,
  fontFamily: mixins.fontFamilyPlusSansPrimary,
  fontWeight: mixins.fontWeight400,
  lineHeight: '22px',
  fontStyle: 'normal'
};

const FOOTER_COPYRIGHT_TYPOGRAPHY = {
  ...FOOTER_LINK_TYPOGRAPHY,
  alignSelf: 'flex-end',
  whiteSpace: 'nowrap'
};

export const FooterContentWrapper = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    bottom: 0,
    zIndex: '3',
    width: '100%',
    minHeight: '293px',
    background: '#F7F9FD',

    '@media screen and (min-width: 833px)': {
      minHeight: '190.91px'
    },
    '@media screen and (min-width: 1194px)': {
      minHeight: '132px'
    }
  }
});

export const FooterContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '30px',
    width: '100%',
    height: '100%',
    padding: '20px 16px',

    '@media screen and (min-width: 1195px)': {
      flexDirection: 'row',
      alignItems: 'center',
      padding: '20px 32px'
    },
    '@media screen and (min-width: 1440px)': {
      padding: '20px 72px'
    }
  }
});

export const ListDiv = styled('div')({
  '&&': {
    flexGrow: 1
  }
});

export const FooterList = styled(List)({
  '&&': {
    display: 'flex',
    padding: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: '30px',

    '@media screen and (min-width: 1195px)': {
      justifyContent: 'center',
      gap: 20
    }
  }
});

export const ListItemStyled = styled(ListItem)({
  '&&': {
    padding: 0,
    cursor: 'pointer',
    width: 'auto'
  }
});

export const FooterAppLink = styled(Link)({
  '&&': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'unset'
    }
  }
});

export const FooterSectionText = styled(Typography)({
  '&&': FOOTER_LINK_TYPOGRAPHY,
  '&&&.MuiTypography-body1': FOOTER_LINK_TYPOGRAPHY
});

export const FooterCopyrightSectionText = styled(Typography)({
  '&&': FOOTER_COPYRIGHT_TYPOGRAPHY,
  '&&&.MuiTypography-body1': FOOTER_COPYRIGHT_TYPOGRAPHY
});

export const FooterLinkAppText = styled(Typography)({
  '&&': {
    fontSize: '14px',
    fontWeight: 'bold',
    marginLeft: '8px',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

export const DownloadAppIcon = styled('div')({
  '&&': {
    flexShrink: 0
  }
});

export const FooterDiv = styled('div')({
  '&&': {
    background: '#E8E6F3',
    position: 'relative',
    display: 'flex',
    height: 92
  }
});

export const FooterContentWrapperVariantB = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    bottom: 0,
    zIndex: '3',
    width: '100%',
    background: mixins.colorFooterBg,
    padding: 16,
    boxSizing: 'border-box',

    '@media screen and (min-width: 768px)': {
      padding: 24
    },
    '@media screen and (min-width: 1024px)': {
      padding: '40px 24px',
      minHeight: '100px'
    },
    '@media screen and (min-width: 1440px)': {
      padding: '40px 32px',
      minHeight: '132px'
    }
  }
});

export const FooterContainerVariantB = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    height: '100%',
    justifyContent: 'space-between',
    '@media screen and (min-width: 1024px)': {
      flexDirection: 'row'
    }
  }
});

export const FooterListVariantB = styled(List)({
  '&&': {
    display: 'flex',
    padding: 0,
    justifyContent: 'flex-start',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: '8px',

    '@media screen and (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '8px'
    }
  }
});

export const FooterSectionVariantBText = styled(Typography)({
  '&&': FOOTER_LINK_TYPOGRAPHY,
  '&&&.MuiTypography-body1': FOOTER_LINK_TYPOGRAPHY
});

export const FooterCopyrightSectionVariantBText = styled(Typography)({
  '&&': {
    ...FOOTER_COPYRIGHT_TYPOGRAPHY,
    '@media screen and (min-width: 1024px)': {
      alignSelf: 'auto'
    }
  },
  '&&&.MuiTypography-body1': {
    ...FOOTER_COPYRIGHT_TYPOGRAPHY,
    '@media screen and (min-width: 1024px)': {
      alignSelf: 'auto'
    }
  }
});

export const ListDivVariantB = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'flex-start'
  }
});

export const FooterLink = styled(Link)({
  '&&': {}
});
